
import * as React from 'react';
import { Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import AppRoutes from './routes/AppRoutes';
import { createLazyComponent } from "./components/LazyPageWrapper";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ThemeProvider as PartnerThemeProvider } from "@/providers/ThemeProvider";
import { AppLayout } from "@/components/layout/AppLayout";

// Lazy load heavy page components for better performance
const LazyTestProcessing = createLazyComponent(() => import("./pages/TestProcessing"), "TestProcessing");
const LazyDashboard = createLazyComponent(() => import("./pages/Dashboard"), "Dashboard");
// Temporary direct import for testing
import Dashboard from "./pages/Dashboard";
//import TestDashboard from "./components/TestDashboard"; 
const LazyPartners = createLazyComponent(() => import("./pages/Partners"), "Partners");
const LazyBills = createLazyComponent(() => import("./pages/Bills"), "Bills");
const LazyProcessing = createLazyComponent(() => import("./pages/Processing"), "Processing");
const LazyBillQueue = createLazyComponent(() => import("./pages/BillQueue"), "BillQueue");
const LazySuppliers = createLazyComponent(() => import("./pages/Suppliers"), "Suppliers");
const LazyReportsPage = createLazyComponent(() => import("./pages/Reports"), "Reports");
const LazyAnalytics = createLazyComponent(() => import("./pages/Analytics"), "Analytics");
const LazyRoleManagement = createLazyComponent(() => import("./pages/RoleManagement"), "RoleManagement");

// Keep frequently used/small components as regular imports for faster access
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import ModernAuth from "./pages/ModernAuth";
import ModernOTPVerification from "./pages/ModernOTPVerification";
import ChangePassword from "./pages/ChangePassword";
import { MobileBillProcessing } from "./pages/MobileBillProcessing";
import { LazyComponentWrapper } from "./components/PerformanceOptimizations";
import Customers from "./pages/Customers";
import Sites from "./pages/Sites";
import Accounts from "./pages/Accounts";
import Meters from "./pages/Meters";
import AIParser from "./pages/AIParser";
import AIAgents from "./pages/AIAgents";
import AutomationPage from "./pages/Automation";
import CollectionWorkflows from "./pages/CollectionWorkflows";
import CollectionPaymentPlans from "./pages/CollectionPaymentPlans";
import CollectionActivities from "./pages/CollectionActivities";
import CollectionDashboard from "./pages/CollectionDashboard";
import CollectionContacts from "./pages/CollectionContacts";
import CollectionScheduling from "./pages/CollectionScheduling";
import SupplierRecommendations from "./pages/SupplierRecommendations";
import IntegrationHub from "./pages/IntegrationHub";
import QualityControl from "./pages/QualityControl";
import SupplierAnalytics from "./pages/SupplierAnalytics";
import RealTimeMonitoring from "./pages/RealTimeMonitoring";
import UserManagement from "./pages/UserManagement";
import DataExport from "./pages/DataExport";
import WorkflowAutomation from "./pages/WorkflowAutomation";
import AdvancedProcessing from "./pages/AdvancedProcessing";
import EnterpriseFeatures from "./pages/EnterpriseFeatures";
import { DashboardLayout } from "./layouts/DashboardLayout";
import PerformancePage from "./pages/Performance";
import PredictiveAnalyticsPage from "./pages/PredictiveAnalytics";
import IntegrationsPage from "./pages/Integrations";
import { PortalManagementPage as PortalManagement } from "./pages/PortalManagement";
import ValidationDashboard from "./pages/ValidationDashboard";
import ReviewDashboard from "./pages/ReviewDashboard";
import AILearning from "./pages/AILearning";
import PhaseManagement from "./pages/PhaseManagement";
import SmartRecommendations from "./pages/SmartRecommendations";
import ModelManagement from "./pages/ModelManagement";
import SystemMonitoring from "./pages/SystemMonitoring";
import ProcessingAnalytics from "./pages/ProcessingAnalytics";
import ProductionOptimization from "./pages/ProductionOptimization";
import API from "./pages/API";
import Security from "./pages/Security";
import AITestCenter from "./pages/AITestCenter";
import AdvancedExtraction from "./pages/AdvancedExtraction";
import ManualParserQueue from "./pages/ManualParserQueue";
import ReviewItem from "./pages/ReviewItem";
import ProductionReview from "./pages/ProductionReview";
import MobileApp from "./pages/MobileApp";
import SecurityCompliance from "./pages/SecurityCompliance";
import MobileFeatures from "./pages/MobileFeatures";
import NotFound from "./pages/NotFound";
import { SafeAuthProvider } from "./components/SafeAuthProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import { WorkspaceRedirect } from "./components/WorkspaceRedirect";
import { WorkspaceErrorBoundary } from '@/components/WorkspaceErrorBoundary';
import { RouteDebugger } from "./components/RouteDebugger";
import Index from "./pages/Index";
import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, Navigate } from 'react-router-dom';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import { getOrCreateDefaultWorkspaceId } from '@/hooks/useDefaultWorkspace';
import AccountProfile from "@/pages/account/AccountProfile";
import AccountSecurity from "@/pages/account/AccountSecurity";
import AccountPassword from "@/pages/account/AccountPassword";
import OTPVerification from '@/pages/OTPVerification';
import NotificationsPage from "@/pages/inbox/NotificationsPage";
import MessagesPage from "@/pages/inbox/MessagesPage";
import RemindersPage from "@/pages/inbox/RemindersPage";
import WorkspaceTeam from "@/pages/workspace/WorkspaceTeam";
import WorkspaceBillingAdvanced from "@/pages/workspace/WorkspaceBillingAdvanced";
import WorkspaceIntegrations from "@/pages/workspace/WorkspaceIntegrations";
import WorkspaceFeatureToggles from "@/pages/workspace/WorkspaceFeatureToggles";
// Workspace management imports
import WorkspaceSettings from "./pages/WorkspaceSettings";
import PartnerBranding from "./pages/PartnerAdmin/Branding";
import WorkspaceModules from "./pages/WorkspaceSettings/Modules";
import OwnerConsole from "./pages/OwnerConsole";
import WorkspaceIsolationTester from "./pages/WorkspaceIsolationTester";
import HealthMonitor from "./pages/workspace/HealthMonitor";
import AccessControlEnhanced from "./pages/workspace/AccessControlEnhanced";
// New module pages
import APIAccess from "./pages/APIAccess";
import Storage from "./pages/Storage";
import Support from "./pages/Support";
import AccessControl from "./pages/AccessControl";
import AccessControlNew from "./pages/AccessControlNew";
import BulkOperations from "./pages/BulkOperations";
import ComplianceDashboard from "./pages/ComplianceDashboard";
import AuditLogViewer from "./pages/AuditLogViewer";
import SecurityMonitoring from "./pages/SecurityMonitoring";
import RoleManagement from "./pages/RoleManagement";
import MultiTenant from "./pages/MultiTenant";
import MultiTenantEnhanced from "./pages/MultiTenantEnhanced";
import PartnerAnalytics from "./pages/SuperAdmin/PartnerAnalytics";
import SystemConfiguration from "./pages/SuperAdmin/SystemConfiguration";
import GlobalUserManagement from "./pages/SuperAdmin/GlobalUserManagement";
import PlatformMonitoring from "./pages/SuperAdmin/PlatformMonitoring";
import PartnerWorkspaces from "./pages/PartnerManagement/PartnerWorkspaces";
import PartnerUsers from "./pages/PartnerManagement/PartnerUsers";
import PartnerModules from "./pages/PartnerManagement/PartnerModules";
import PartnerSubdomains from "./pages/PartnerManagement/PartnerSubdomains";
import PartnerSubdomainManager from "./components/PartnerSubdomainManager";
import { DomainDashboard } from "./pages/PartnerManagement/DomainDashboard";
import AcceptInvite from "./pages/invite/AcceptInvite";
import { SelectWorkspace } from "./pages/SelectWorkspace";
import { InviteAcceptWrapper } from "./components/InviteAcceptWrapper";
import { TrialManagementPage } from "./pages/TrialManagementPage";
import EnterpriseAnalytics from "./pages/EnterpriseAnalytics";

import { StripeProvider } from "@/contexts/StripeProvider";
import { WorkspaceProvider } from "./contexts/WorkspaceContext";
import { WorkspaceLoadingSpinner } from "./components/WorkspaceLoadingSpinner";
import PartnerWorkspaceRedirect from "./components/PartnerWorkspaceRedirect";

// DefaultWorkspaceRedirect component
function DefaultWorkspaceRedirect() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading your workspace...");

  useEffect(() => {
    (async () => {
      if (!user?.id) return;
      
      try {
        setLoadingMessage("Finding your workspace...");
        const id = await getOrCreateDefaultWorkspaceId(user.id, user.email!);
        console.log('[DEFAULT_WS] Redirecting to workspace:', id);
        setLoadingMessage("Redirecting to dashboard...");
        navigate(`/w/${id}/dashboard`, { replace: true });
        setDone(true);
      } catch (error) {
        console.error('[DEFAULT_WS] Error creating/finding workspace:', error);
        // If no workspaces are available, redirect to workspace selection
        if (error instanceof Error && error.message === 'NO_WORKSPACES_AVAILABLE') {
          console.log('[DEFAULT_WS] No workspaces available, redirecting to selection');
          setLoadingMessage("Setting up your workspace...");
          navigate('/select-workspace', { replace: true });
        } else {
          console.error('[DEFAULT_WS] Unexpected error:', error);
          setLoadingMessage("Something went wrong, redirecting...");
          navigate('/select-workspace', { replace: true });
        }
        setDone(true);
      }
    })();
  }, [user?.id, navigate]);

  if (!user) return <Navigate to="/auth" replace />;
  
  if (done) return null;
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="text-center p-8 bg-white rounded-lg shadow-lg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Welcome to BillPortal</h2>
        <p className="text-muted-foreground">{loadingMessage}</p>
        <p className="text-sm text-gray-500 mt-2">Setting up your personalized dashboard...</p>
      </div>
    </div>
  );
}

// WorkspaceRoute wrapper - CRITICAL: Provides workspace context inside the route
function WorkspaceRoute() {
  return (
    <WorkspaceErrorBoundary>
      <WorkspaceProvider>
        <DashboardLayout />
      </WorkspaceProvider>
    </WorkspaceErrorBoundary>
  );
}

// Environment setup - API connection configured
console.log('✅ Using configured API connection');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30,
      refetchOnWindowFocus: false,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    },
    mutations: {
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    },
  },
});

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <SafeAuthProvider>
        <QueryClientProvider client={queryClient}>
          <StripeProvider>
            <ThemeProvider>
              <PartnerThemeProvider>
                <AppRoutes />
              </PartnerThemeProvider>
            </ThemeProvider>
          </StripeProvider>
        </QueryClientProvider>
      </SafeAuthProvider>
    </BrowserRouter>
  );
};

export default App;
